import { useState, useCallback, useEffect } from 'react';
import reactCSS from 'reactcss';

import classnames from 'classnames';

import { Typography, Button, Col, Row } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { FormCancelButton, FormSaveButton } from 'common/components';
import { QaSpecHelpContentLabel } from 'pages/qa-spec/components/shared/QaSpecHelpContent';
import {
  HighlightContent,
  RetailerSnapshot,
} from 'pages/qa-spec/components/qa-prod-spec/snapshot/RetailerSnapshot';
import { QaSpecBoolean } from 'pages/qa-spec/components/shared/QaSpecBoolean';
import { EditTabSectionWrapper } from 'pages/qa-spec/components/edit-tab-checker';
import { useCheckAllowEdit } from 'pages/qa-spec/hooks';

import { useCheckQaSpecLocked } from 'pages/qa-spec/hooks/useCheckQaSpecLocked';

import './WithQaGridHeader.less';
import TraceableCommentContainer from 'pages/traceable-comment/TraceableCommentContainer';
import { COMMENT_FIELDS } from 'static/QaSpecFields';

const { Title } = Typography;

const styles = reactCSS({
  default: {
    buttonCol: {
      display: 'flex',
      alignItems: 'flex-end',
    },
  },
});

const WithQaGridHeader = (props) => {
  const {
    title,
    children,
    show = true,
    buttons,
    extraButtons,
    allowEdit = true,
    handleCancel,
    showSaveBtn = true,
    showCancelBtn = true,
    showEditBtn = true,
    forceEdit,
    handleSave,
    showHeader = true,
    // TODO: Update the property name from "AnalysisNotExecuted" to "AnalysisExecuted" in code base.
    isShowAnalysisNotExecuted = false,
    analysisNotExecuted, // TODO: Update it to analysisExecuted.
    analysisNotExecutedSnapshot, // TODO: Update it to analysisExecutedSnapshot.
    fieldPathTooltip = '',
    type,
    productId,
    onEditChange,
    isShowSubtitle = false,
    subtitle
  } = props;

  const [isEdit, setIsEdit] = useState(false);
  const [isAnalysis, setIsAnalysis] = useState(analysisNotExecuted);

  const toggleEditMode = useCallback(() => {
    setIsEdit((prev) => !prev);
  }, []);

  const onSave = () => {
    handleSave && handleSave(toggleEditMode, isAnalysis);
  };

  useEffect(() => {
    isShowAnalysisNotExecuted && setIsAnalysis(analysisNotExecuted);
  }, [analysisNotExecuted, isShowAnalysisNotExecuted]);

  const { isQASpecLocked } = useCheckQaSpecLocked();

  const onCancel = () => {
    isShowAnalysisNotExecuted && setIsAnalysis(analysisNotExecuted);
    toggleEditMode();
    handleCancel && handleCancel();
  };

  const hasPermissionEdit = useCheckAllowEdit(productId);

  const handleChange = (e) => {
    setIsAnalysis(e.target.value);
  };

  useEffect(() => {
    setIsEdit(forceEdit);
  }, [forceEdit]);

  useEffect(() => {
    onEditChange && onEditChange(isEdit);
  }, [isEdit]);

  return (
    <>
      <div
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
        className='qa-agGrid-edit-header'
      >
        {showHeader && (
          <Row
            style={{ minHeight: 27, marginBottom: 7, flexFlow: 'row' }}
            gutter={[5, 5]}
          >
            <Col
              span={isShowAnalysisNotExecuted && 6}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              {title && (
                <Title level={5} strong style={{ marginBottom: 0 }}>
                  {title}
                </Title>
              )}
            </Col>

            {isShowAnalysisNotExecuted && (
              <Col flex='auto'>
                <Row style={{ width: '100%' }}>
                  <Col
                    flex='200px'
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <TraceableCommentContainer
                      qaSpecName={
                        COMMENT_FIELDS.productSpecification.qaSpecName
                      }
                      qaSpecFieldName={
                        title.props.children[1] + ' Analysis Executed'
                      }
                    >
                      <QaSpecHelpContentLabel fieldPath={fieldPathTooltip}>
                        <Title level={5} strong style={{ marginBottom: 0 }}>
                          Analysis Executed
                        </Title>
                      </QaSpecHelpContentLabel>
                    </TraceableCommentContainer>
                  </Col>
                  <Col flex='auto'>
                    <RetailerSnapshot
                      supplierModifiedValue={analysisNotExecuted}
                      snapshotValue={analysisNotExecutedSnapshot}
                      highlightContent={
                        <HighlightContent
                          label='Analysis Executed'
                          value={analysisNotExecutedSnapshot}
                          variant='checkbox'
                        />
                      }
                    >
                      <QaSpecBoolean
                        className={classnames({
                          'qa-agGrid-edit-header__checkbox': !isEdit,
                          'retailer-snapshot__radio': true,
                        })}
                        onChange={handleChange}
                        value={isAnalysis}
                      />
                    </RetailerSnapshot>
                  </Col>
                </Row>
              </Col>
            )}

            <Col flex='auto' />
            {!isQASpecLocked ? (
              <>
                <Col style={styles.buttonCol}>
                  {showSaveBtn && isEdit && (
                    <EditTabSectionWrapper.SaveActionTriggerWrapper
                      type={type}
                      onSave={onSave}
                    >
                      <FormSaveButton htmlType='submit' onClick={onSave} />
                    </EditTabSectionWrapper.SaveActionTriggerWrapper>
                  )}
                </Col>
                <Col style={styles.buttonCol}>
                  {showEditBtn &&
                    show &&
                    !isEdit &&
                    allowEdit &&
                    hasPermissionEdit && (
                      <Button
                        className='qa-agGrid-edit-header__edit-btn'
                        icon={<EditOutlined />}
                        style={{ border: 'none' }}
                        onClick={toggleEditMode}
                      />
                    )}
                  {showCancelBtn && isEdit && (
                    <EditTabSectionWrapper.CancelActionTriggerWrapper
                      type={type}
                      onCancel={onCancel}
                    >
                      <FormCancelButton onClick={onCancel} />
                    </EditTabSectionWrapper.CancelActionTriggerWrapper>
                  )}
                </Col>
                {extraButtons?.map((buttonItem, idx) => (
                  <Col style={styles.buttonCol} key={idx}>
                    {buttonItem(isEdit)}
                  </Col>
                ))}

                {isEdit && (
                  <>
                    {buttons?.map((buttonItem, idx) =>
                      buttonItem ? (
                        <Col style={styles.buttonCol} key={idx}>
                          {buttonItem(isEdit)}
                        </Col>
                      ) : null
                    )}
                  </>
                )}
              </>
            ) : null}
          </Row>
        )}
        {isShowSubtitle && (
          <Row style={{ width: '100%' }}>
            <Col>
              {subtitle}
            </Col>
          </Row>
        )}
        <Row style={{ flex: 1 }}>
          <Col xs={24}>
            {typeof children === 'function' ? children(isEdit) : children}
          </Col>
        </Row>
      </div>
    </>
  );
};

export default WithQaGridHeader;
